import { ICellRendererParams } from 'ag-grid-enterprise'
import React, { useState, useMemo, useCallback } from 'react'
import { CustomReportVariable } from '../../../../../redux/context/customReports/typesVariable'
import { getFormattedValue } from './utils/valueFormatting'
import { usePermission } from '../../../../../redux/session/currentUser/hooks'
import { showArrow } from '../customReportTable/utils'
import Arrow from '../../../../../components/Misc/Arrow'
import { CustomColumnContext } from './types'
import { canShowContextMenu } from './utils/contextMenuUtils'
import Restricted from '../customReportTable/Restricted'

type CustomValueCellRendererProps = ICellRendererParams<CustomReportVariable, any>

const CustomValueCellRenderer: React.FC<CustomValueCellRendererProps> = props => {
  const [isHovered, setIsHovered] = useState(false)
  const canReadVouchers = usePermission('read-vouchers')
  const { data, value, colDef } = props
  const colContext = colDef?.context as CustomColumnContext
  const canShowMenu = useMemo((): boolean => {
    if (!data) return false
    return canShowContextMenu(data, colContext, canReadVouchers)
  }, [data, value, colContext, canReadVouchers])

  const formattedValue = useMemo(
    () => getFormattedValue(value, data ?? undefined, colContext?.category),
    [value, data, colContext?.category]
  )

  const errors = useMemo(() => {
    // Find errors for this category/variable combination
    const balance = data?.balances?.find(b => b.groupId === colContext?.category?.id)
    return balance?.errors || []
  }, [data, colContext])

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])

  const cellStyle = useMemo(
    () => ({
      cursor: canShowMenu ? 'pointer' : 'default',
      color: isHovered && canShowMenu ? '#2563eb' : 'inherit',
      transition: 'all 0.2s ease',

      ...(isHovered &&
        canShowMenu && {
          textDecorationColor: '#2563eb',
          textUnderlineOffset: '2px'
        })
    }),
    [isHovered, canShowMenu]
  )

  // Show Restricted component if there are errors and no value
  if (errors.length > 0 && (value === undefined || value === null)) {
    return <Restricted errors={errors} />
  }

  return (
    <div style={cellStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {data && showArrow(value, colContext.category, data) && value && (
        <Arrow value={value} thresholdValueSetting={data?.style?.thresholdValueSetting} />
      )}
      <span>{formattedValue}</span>
    </div>
  )
}

export default CustomValueCellRenderer
